import { convolveReal } from '../dsp/fft.ts';
import { distanceToDelay } from '../dsp/acoustics.ts';
import { fromDb, maxAbs } from '../dsp/util.ts';

/**
 * A synthetic room, for testing the analysis chain against a known answer.
 *
 * Every number the app reports - RT60, volume, echo distance - can be checked
 * here against ground truth before a real microphone is ever involved. It is
 * also what powers the app's "Demo (no mic)" path.
 */

export interface Reflector {
  distanceM: number;
  /** Reflection strength relative to the direct sound, in dB (negative). */
  levelDb: number;
}

export interface SyntheticRoomSpec {
  sampleRate: number;
  rt60Sec: number;
  reflectors: Reflector[];
  /** Level of the diffuse reverb tail at t=0, relative to direct, in dB. */
  diffuseLevelDb: number;
  /** How much impulse response to generate. */
  lengthSec: number;
  /** How long the diffuse field takes to build. Defaults to the first reflection. */
  mixingTimeSec?: number;
  temperatureC?: number;
  seed?: number;
}

export interface RecordingOptions {
  /** Signal-to-noise ratio of the capture, in dB. */
  snrDb?: number;
  /**
   * Third-order distortion coefficient of the "speaker". Non-zero pushes
   * harmonic products into the deconvolution, where they should land at
   * negative time and stay out of the way.
   */
  distortion?: number;
  /** Extra silence before the sweep, mimicking playback latency. */
  preRollSec?: number;
  /**
   * Peak level of the capture, in dBFS. Real captures are gain-staged so the
   * direct blast does not hit the rails; leaving the simulated one at whatever
   * the convolution produces makes live rooms "clip" for no physical reason.
   */
  peakDbfs?: number;
  seed?: number;
}

function defaultMixingTime(spec: SyntheticRoomSpec): number {
  const nearest = spec.reflectors.reduce(
    (min, r) => Math.min(min, distanceToDelay(r.distanceM, spec.temperatureC)),
    Infinity,
  );
  return Math.max(0.004, Number.isFinite(nearest) ? nearest : 0.01);
}

/** Deterministic PRNG so tests do not flake. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Box-Muller normal deviate from a uniform source. */
function gaussian(rand: () => number): number {
  const u = Math.max(rand(), 1e-12);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * rand());
}

/** Impulse response of the synthetic room, with the direct sound at index 0. */
export function synthesizeImpulseResponse(spec: SyntheticRoomSpec): Float64Array {
  const { sampleRate, rt60Sec } = spec;
  const n = Math.round(spec.lengthSec * sampleRate);
  const ir = new Float64Array(n);
  const rand = mulberry32(spec.seed ?? 12345);

  ir[0] = 1;

  for (const reflector of spec.reflectors) {
    const delaySamples = distanceToDelay(reflector.distanceM, spec.temperatureC) * sampleRate;
    addFractionalImpulse(ir, delaySamples, fromDb(reflector.levelDb));
  }

  // Exponentially decaying noise: -60 dB of amplitude over exactly rt60.
  //
  // Ramped in over a mixing time rather than starting at full level at t=0.
  // Real reverberation has to build: reflection density grows with time, and
  // the moment right after the direct sound is nearly empty. A tail that
  // starts instantly is not just unrealistic, it makes the simulator lie -
  // it can put more energy in the first millisecond than the direct sound
  // has, which no co-located source and receiver can produce.
  const decay = 6.907755 / rt60Sec;
  const level = fromDb(spec.diffuseLevelDb);
  const mixingTimeSec = spec.mixingTimeSec ?? defaultMixingTime(spec);
  for (let i = 1; i < n; i++) {
    const t = i / sampleRate;
    const buildup = 1 - Math.exp(-t / mixingTimeSec);
    ir[i] += gaussian(rand) * level * Math.exp(-decay * t) * buildup;
  }

  return ir;
}

/**
 * Band-limited fractional-delay impulse (windowed sinc).
 *
 * Rounding to the nearest sample would cap range accuracy at +/-1.8 mm and,
 * worse, would hide errors in the sub-sample peak interpolation the range
 * profile depends on.
 */
function addFractionalImpulse(target: Float64Array, delaySamples: number, amplitude: number): void {
  const taps = 32;
  const center = Math.floor(delaySamples);
  const frac = delaySamples - center;
  for (let k = -taps / 2; k <= taps / 2; k++) {
    const index = center + k;
    if (index < 0 || index >= target.length) continue;
    const x = k - frac;
    const sinc = Math.abs(x) < 1e-9 ? 1 : Math.sin(Math.PI * x) / (Math.PI * x);
    // Blackman window over the tap span.
    const w = 0.42 + 0.5 * Math.cos((2 * Math.PI * x) / (taps + 1)) + 0.08 * Math.cos((4 * Math.PI * x) / (taps + 1));
    target[index] += amplitude * sinc * w;
  }
}

/**
 * What the microphone would hear: the sweep through the room, plus playback
 * latency, speaker distortion and a noise floor.
 */
export function synthesizeRecording(
  sweepSamples: ArrayLike<number>,
  ir: Float64Array,
  sampleRate: number,
  options: RecordingOptions = {},
): Float64Array {
  const snrDb = options.snrDb ?? 40;
  const distortion = options.distortion ?? 0;
  const preRoll = Math.round((options.preRollSec ?? 0.05) * sampleRate);
  const rand = mulberry32(options.seed ?? 999);

  const driven = new Float64Array(sweepSamples.length);
  for (let i = 0; i < sweepSamples.length; i++) {
    const x = sweepSamples[i];
    driven[i] = distortion === 0 ? x : x - distortion * x * x * x;
  }

  const wet = convolveReal(driven, ir);

  const peakDbfs = options.peakDbfs ?? -6;
  const peak = maxAbs(wet);
  if (peak > 0) {
    const gain = fromDb(peakDbfs) / peak;
    for (let i = 0; i < wet.length; i++) wet[i] *= gain;
  }

  let signalPower = 0;
  for (let i = 0; i < wet.length; i++) signalPower += wet[i] * wet[i];
  signalPower /= Math.max(1, wet.length);
  const noiseAmplitude = Math.sqrt(signalPower) * fromDb(-snrDb);

  const out = new Float64Array(preRoll + wet.length + Math.round(0.05 * sampleRate));
  for (let i = 0; i < wet.length; i++) out[preRoll + i] = wet[i];
  for (let i = 0; i < out.length; i++) out[i] += gaussian(rand) * noiseAmplitude;
  return out;
}

/**
 * Reflection level relative to the direct sound, from spherical spreading.
 *
 * This is the number that sets the whole design. The direct blast travels the
 * speaker-to-microphone spacing - about 5 cm - while an echo off a wall 1.15 m
 * away travels 2.3 m. Inverse-square spreading alone puts that echo 33 dB
 * down before the wall has absorbed anything. Room echoes are not "a bit
 * quieter than" the direct sound; they are 30 to 50 dB below it, which is why
 * the skirts of the direct blast matter more than any other number here.
 */
export function reflectionLevelDb(distanceM: number, reflectionLossDb = 3, spacingM = 0.05): number {
  return 20 * Math.log10(spacingM / (2 * distanceM)) - reflectionLossDb;
}

/**
 * Rooms worth comparing, with reflection levels derived from their geometry.
 *
 * The first three share their names and their acoustics with the three-
 * dimensional scenes the map demo reconstructs, so picking a demo room picks
 * the same room in both modes rather than two unrelated ones.
 *
 * The diffuse level sits below the first specular reflection, because that is
 * the causal order: the early reflections are what the diffuse field is built
 * out of. A tail set louder than the reflections that feed it buries them,
 * which is a simulator artefact rather than a room.
 */
export const DEMO_ROOMS = {
  bedroom: {
    rt60Sec: 0.42,
    diffuseLevelDb: -46,
    seed: 11,
    reflectors: [
      { distanceM: 1.15, levelDb: reflectionLevelDb(1.15, 4) },
      { distanceM: 2.4, levelDb: reflectionLevelDb(2.4, 4) },
      { distanceM: 3.7, levelDb: reflectionLevelDb(3.7, 5) },
    ],
  },
  bathroom: {
    rt60Sec: 1.15,
    diffuseLevelDb: -40,
    seed: 22,
    reflectors: [
      { distanceM: 0.85, levelDb: reflectionLevelDb(0.85, 1) },
      { distanceM: 1.6, levelDb: reflectionLevelDb(1.6, 1) },
      { distanceM: 2.2, levelDb: reflectionLevelDb(2.2, 1) },
    ],
  },
  office: {
    rt60Sec: 0.55,
    diffuseLevelDb: -46,
    seed: 44,
    reflectors: [
      { distanceM: 1.5, levelDb: reflectionLevelDb(1.5, 4) },
      { distanceM: 2.2, levelDb: reflectionLevelDb(2.2, 4) },
      { distanceM: 3.0, levelDb: reflectionLevelDb(3.0, 4) },
    ],
  },
  kitchen: {
    rt60Sec: 0.75,
    diffuseLevelDb: -42,
    seed: 55,
    reflectors: [
      { distanceM: 1.2, levelDb: reflectionLevelDb(1.2, 2) },
      { distanceM: 1.6, levelDb: reflectionLevelDb(1.6, 2) },
      { distanceM: 2.0, levelDb: reflectionLevelDb(2.0, 2) },
    ],
  },
  stairwell: {
    rt60Sec: 2.6,
    diffuseLevelDb: -45,
    seed: 33,
    reflectors: [
      { distanceM: 1.4, levelDb: reflectionLevelDb(1.4, 2) },
      { distanceM: 4.2, levelDb: reflectionLevelDb(4.2, 2) },
      { distanceM: 8.5, levelDb: reflectionLevelDb(8.5, 3) },
    ],
  },
} satisfies Record<string, Omit<SyntheticRoomSpec, 'sampleRate' | 'lengthSec'>>;

export type DemoRoomName = keyof typeof DEMO_ROOMS;
